import { z } from 'zod';
import {
  CountryCodeSchema,
  IntentSchema,
  IsoDateTimeSchema,
  LanguageCodeSchema,
  UuidSchema,
} from './common.js';

export const KeywordRunStatusSchema = z.enum([
  'queued',
  'expanding',
  'enriching',
  'classifying',
  'clustering',
  'scoring',
  'completed',
  'failed',
  'cancelled',
]);
export type KeywordRunStatus = z.infer<typeof KeywordRunStatusSchema>;

export const KeywordSeedSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('domain'), value: z.string().url() }),
  z.object({ type: z.literal('topics'), value: z.array(z.string().min(1)).min(1).max(50) }),
  z.object({ type: z.literal('description'), value: z.string().min(20).max(2000) }),
]);
export type KeywordSeed = z.infer<typeof KeywordSeedSchema>;

export const CreateKeywordRunRequestSchema = z.object({
  project_id: UuidSchema,
  seed: KeywordSeedSchema,
  country_code: CountryCodeSchema.default('IN'),
  language: LanguageCodeSchema.default('en'),
  smart_mode: z.boolean().default(false),
  reuse_audit: z.boolean().default(true),
});
export type CreateKeywordRunRequest = z.infer<typeof CreateKeywordRunRequestSchema>;

export const KeywordRunWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
});
export type KeywordRunWarning = z.infer<typeof KeywordRunWarningSchema>;

export const KeywordRunSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  seed: KeywordSeedSchema,
  country_code: CountryCodeSchema,
  language: LanguageCodeSchema,
  status: KeywordRunStatusSchema,
  warnings: z.array(KeywordRunWarningSchema).default([]),
  started_at: IsoDateTimeSchema.nullable(),
  completed_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  business_summary: z.string().nullable().default(null),
  created_at: IsoDateTimeSchema,
});
export type KeywordRun = z.infer<typeof KeywordRunSchema>;

export const KeywordSchema = z.object({
  id: UuidSchema,
  keyword_run_id: UuidSchema,
  cluster_id: UuidSchema.nullable(),
  term: z.string(),
  volume: z.number().int().nullable(),
  cpc_cents: z.number().int().nullable(),
  competition_index: z.number().int().min(0).max(100).nullable(),
  intent: IntentSchema.nullable(),
  difficulty: z.number().int().min(0).max(100).nullable(),
  opportunity: z.number().int().nullable(),
  serp_features: z.array(z.string()),
  /**
   * A2 v2 masterclass signal — top-10 domains from the live SERP snapshot,
   * ordered by rank. Empty when the SERP fetch didn't run (free-tier fallback).
   */
  serp_top_domains: z.array(z.string()).default([]),
  /**
   * A2 v2 masterclass signal — SERP domain-authority proxy (0..100) derived
   * from `serp_top_domains` via `estimateDomainAuthority()`. Higher = more
   * entrenched competitors.
   */
  serp_avg_da: z.number().int().min(0).max(100).nullable(),
  /**
   * Own-domain organic position from DataForSEO Labs ranked_keywords.
   * Null = not ranking in the fetched set (typically outside top 20) — a gap.
   */
  your_position: z.number().int().min(1).max(100).nullable(),
  /**
   * Latest observed position from the Tracking module, when this keyword is
   * being tracked in `tracked_keywords` for the same project. Populated at
   * list-response time via a join on keyword_id / normalized-term. Uses the
   * tracker's provider (Serper / DataForSEO) — independent from the
   * keyword-run's DFS-only `your_position`. Undefined on responses that
   * don't perform the enrichment (e.g., worker-side reads).
   */
  tracked_position: z.number().int().min(1).max(100).nullable().optional(),
  tracked_provider: z.string().nullable().optional(),
  tracked_checked_at: IsoDateTimeSchema.nullable().optional(),
  source: z.string().nullable(),
});
export type Keyword = z.infer<typeof KeywordSchema>;

export const KeywordClusterSchema = z.object({
  id: UuidSchema,
  keyword_run_id: UuidSchema,
  parent_cluster_id: UuidSchema.nullable(),
  label: z.string(),
  description: z.string().nullable(),
  size: z.number().int(),
  total_volume: z.number().int(),
  avg_difficulty: z.number().int().nullable(),
  /**
   * A2 v2 masterclass — the keyword nominated as the cluster's "pillar":
   * best volume × intent × opportunity combination. Feeds the pillar-only
   * filter in the UI and the A3 brief workflow.
   */
  pillar_keyword_id: UuidSchema.nullable(),
});
export type KeywordCluster = z.infer<typeof KeywordClusterSchema>;

/** LLM output for intent batch classification. */
export const IntentBatchOutputSchema = z.object({
  classifications: z.array(
    z.object({
      term: z.string(),
      intent: IntentSchema,
    }),
  ),
});
export type IntentBatchOutput = z.infer<typeof IntentBatchOutputSchema>;

/** LLM output for cluster labeling. */
export const ClusterLabelOutputSchema = z.object({
  label: z.string().min(2).max(64),
  description: z.string().min(10).max(280),
});
export type ClusterLabelOutput = z.infer<typeof ClusterLabelOutputSchema>;

/**
 * Phase 1 smart-seed — LLM structured output for the seed-enrichment step.
 * Consumed by both the LLM call (as the response schema) and the worker
 * activity types (as the return shape of enrichSeed).
 */
export const SeedEnrichOutputSchema = z.object({
  business_summary: z.string().min(20).max(300).describe('One-line description of what this site does.'),
  seeds: z.object({
    informational: z.array(z.string().min(2).max(80)).max(30).default([]),
    commercial: z.array(z.string().min(2).max(80)).max(30).default([]),
    transactional: z.array(z.string().min(2).max(80)).max(20).default([]),
    navigational: z.array(z.string().min(2).max(80)).max(10).default([]),
  }),
});
export type SeedEnrichOutput = z.infer<typeof SeedEnrichOutputSchema>;

/**
 * Phase 1 keyword-picker — single search hit shape returned by
 * GET /v1/projects/:projectId/keywords/search. Denormalizes cluster label +
 * run status so the UI doesn't need to fetch a second time to render metadata
 * chips.
 */
export const KeywordSearchResultSchema = z.object({
  id: UuidSchema,
  term: z.string(),
  keyword_run_id: UuidSchema,
  run_status: KeywordRunStatusSchema,
  volume: z.number().int().nullable(),
  difficulty: z.number().int().min(0).max(100).nullable(),
  intent: IntentSchema.nullable(),
  cluster_id: UuidSchema.nullable(),
  cluster_label: z.string().nullable(),
});
export type KeywordSearchResult = z.infer<typeof KeywordSearchResultSchema>;

export const KeywordSearchResponseSchema = z.object({
  items: z.array(KeywordSearchResultSchema),
  has_more: z.boolean(),
});
export type KeywordSearchResponse = z.infer<typeof KeywordSearchResponseSchema>;
